import { useCallback, useEffect, useState } from 'react';
import { getEncryptedLectureVideo } from '../api/learningApi';
import { extractYouTubeVideoId } from '../api/videoUrlCrypto';

async function decryptLectureVideo(lectureId, signal) {
  const response = await getEncryptedLectureVideo(lectureId, { signal });

  if (signal.aborted) return null;
  const videoId = extractYouTubeVideoId(response?.videoUrl);
  if (!videoId) {
    throw new Error('The video server returned an invalid YouTube ID.');
  }

  return videoId;
}

export default function useEncryptedLectureVideo(lectureId) {
  const [videoId, setVideoId] = useState(null);
  const [loading, setLoading] = useState(Boolean(lectureId));
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!lectureId) {
      setVideoId(null);
      setLoading(false);
      setError('No lesson was selected.');
      return undefined;
    }

    const controller = new AbortController();
    setVideoId(null);
    setLoading(true);
    setError('');

    decryptLectureVideo(lectureId, controller.signal)
      .then((decryptedVideoId) => {
        if (!controller.signal.aborted) {
          setVideoId(decryptedVideoId);
          setLoading(false);
        }
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setError(requestError.message || 'Unable to load this lesson video.');
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [lectureId, attempt]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);
  return { videoId, loading, error, retry };
}
